import { useMemo } from 'react'

import type { Route } from '../types/routeTypes'
import { useGrowthBook } from '../utils/growthbook/generated'
import { PAGES_CACHE } from '../utils/navigation'
import { encodeQuery } from '../utils/query'

import { filterRoutesByConfig } from './useNavigation.Utils'
import { useRouter } from './useRouter'

/**
 * Initializes the navigation cache with allowed routes based on the configuration object.
 */
export const useNavigation = (
  routes: Route<string>[],
  promoCode: string | undefined,
  funnelId: string | undefined
): Route<string>[] => {
  const growthBook = useGrowthBook()
  const { resolvedPathname } = useRouter()

  return useMemo(() => {
    // Filter the routes based on the config flag and return a new array of pages with encoded promo code.
    const filteredRoutes = filterRoutesByConfig({
      routes,
      currentPath: resolvedPathname,
      funnelId,
      evalFeature: growthBook.evalFeature,
    })

    const encodedRoutes = filteredRoutes.map((route) => ({
      ...route,
      asPath: encodeQuery(route.path, { promo: promoCode }),
    }))

    // Set the cache with the new array of pages.
    PAGES_CACHE.length = 0
    PAGES_CACHE.push(...encodedRoutes)

    return PAGES_CACHE.map((route) => route || undefined).filter(Boolean)
  }, [funnelId, promoCode, routes, growthBook, resolvedPathname])
}
